import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  QUALIFICATIONS,
  GRADES_BY_QUALIFICATION,
  SUBJECTS_BY_QUALIFICATION,
  QUALIFICATION_LABEL,
} from "@/lib/qualifications";

/**
 * PredictedGradesPicker
 * ---------------------
 * A compact 3-field row (qualification / subject / grade) + Add
 * button that appends to a chip list. Callers pass in an array of
 * {qualification, subject, grade} objects and an onChange handler.
 *
 *  - Subject field is a free text input with a datalist of curated
 *    suggestions filtered by qualification, so students can pick a
 *    canonical name OR type their own.
 *  - Max 5 entries to keep the noise down. The Add button disables
 *    once the ceiling is hit.
 *  - Compact-mode (`compact`) shrinks paddings for the shortlist
 *    widget while `full` is used on `/profile`.
 */
export default function PredictedGradesPicker({
  value = [],
  onChange,
  onPending,
  compact = false,
  testIdPrefix = "predicted-grades",
}) {
  const [qual, setQual]       = useState("a_level");
  const [subject, setSubject] = useState("");
  const [grade, setGrade]     = useState("");
  const subjectRef = useRef(null);

  const grades   = GRADES_BY_QUALIFICATION[qual] || [];
  const subjects = SUBJECTS_BY_QUALIFICATION[qual] || [];
  const capped   = (value || []).length >= 5;

  // The datalist ID must be unique per instance so multiple pickers
  // on the same page don't share suggestions.
  const datalistId = useMemo(() => `${testIdPrefix}-subjects-${qual}`, [testIdPrefix, qual]);

  const canAdd = qual && subject.trim() && grade && !capped;

  // Let the parent's Save include a fully filled row the student hasn't added with "+" yet.
  useEffect(() => {
    if (!onPending) return;
    const row = { qualification: qual, subject: subject.trim(), grade };
    onPending(canAdd ? () => { setSubject(""); setGrade(""); return [...(value || []), row]; } : null, canAdd ? [...(value || []), row] : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAdd, qual, subject, grade, value]);

  const add = () => {
    if (!canAdd) return;
    const next = [...(value || []), {
      qualification: qual,
      subject:       subject.trim(),
      grade,
    }];
    onChange(next);
    setSubject("");
    setGrade("");
    subjectRef.current?.focus();
  };

  const remove = (idx) => {
    const next = (value || []).filter((_, i) => i !== idx);
    onChange(next.length ? next : undefined);
  };

  const inputCls = compact
    ? "border border-stone-300 rounded-md px-2 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
    : "border border-stone-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500";

  const hCls = compact ? " h-8" : " h-9";

  return (
    <div data-testid={testIdPrefix} className="w-full">
      {(value || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testIdPrefix}-chips`}>
          {(value || []).map((row, i) => (
            <span
              key={`${row.qualification}-${row.subject}-${i}`}
              data-testid={`${testIdPrefix}-chip-${i}`}
              className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-900 text-[12px] px-2 py-0.5 rounded-full"
            >
              <span className="font-mono font-semibold">{row.grade}</span>
              <span>·</span>
              <span>{row.subject}</span>
              <span className="text-emerald-700 text-[10px]">
                {QUALIFICATION_LABEL[row.qualification] || row.qualification}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-emerald-700 hover:text-emerald-900 ml-0.5"
                aria-label={`Remove ${row.subject}`}
                data-testid={`${testIdPrefix}-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-4"} gap-2 items-start`}>
        <label className="block">
          <span className="text-[11px] font-medium text-stone-600 block mb-1">Qualification</span>
          <select
            className={inputCls + " w-full" + hCls}
            value={qual}
            data-testid={`${testIdPrefix}-qualification`}
            onChange={e => { setQual(e.target.value); setGrade(""); }}
          >
            {QUALIFICATIONS.map(q => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </label>

        <label className="block sm:col-span-2">
          <span className="text-[11px] font-medium text-stone-600 block mb-1">Subject</span>
          <input
            ref={subjectRef}
            type="text"
            list={datalistId}
            className={inputCls + " w-full" + hCls}
            value={subject}
            placeholder="e.g. Mathematics"
            data-testid={`${testIdPrefix}-subject-input`}
            onChange={e => setSubject(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && canAdd) { e.preventDefault(); add(); }
            }}
          />
          <datalist id={datalistId}>
            {subjects.map(s => <option key={s} value={s} />)}
          </datalist>
        </label>

        <label className="block">
          <span className="text-[11px] font-medium text-stone-600 block mb-1">Predicted grade</span>
          <div className="flex gap-1">
            <select
              className={inputCls + " flex-1 min-w-0" + hCls}
              value={grade}
              data-testid={`${testIdPrefix}-grade`}
              onChange={e => setGrade(e.target.value)}
            >
              <option value="">—</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <button
              type="button"
              onClick={add}
              disabled={!canAdd}
              data-testid={`${testIdPrefix}-add`}
              className={`inline-flex ${hCls} shrink-0 items-center justify-center rounded-md px-2 ${
                canAdd
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-stone-200 text-stone-400 cursor-not-allowed"
              }`}
              aria-label="Add subject"
              title={capped ? "Max 5 subjects" : "Add subject"}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </label>
      </div>

      {capped && (
        <div className="text-[11px] text-stone-500 mt-1" data-testid={`${testIdPrefix}-capped`}>
          Max 5 subjects — remove one to add another.
        </div>
      )}
    </div>
  );
}
